import { useEffect, useState } from 'react'
import { Clock, AlertTriangle, Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { BusinessDay } from '@/types'

interface DailyClosingBannerProps {
  currentDay: BusinessDay | null | undefined
  closeoutHour: number
  userRole: string
  onCloseClick: () => void
  onGetManagerClick: () => void
}

/**
 * Time- and role-aware nudge banner. Renders below the counter header
 * starting ~4h before the configured closeout hour and escalates amber
 * → red as the deadline approaches. Manager/admin sees a
 * "Close now" CTA that jumps to Daily Closing; everyone else sees a
 * "Get manager" CTA that opens the PIN handoff modal.
 *
 * Hidden when:
 *   • no day exists yet (manager hasn't opened),
 *   • the day is already closed,
 *   • more than 4 hours remain to the closeout hour.
 */
export function DailyClosingBanner({
  currentDay,
  closeoutHour,
  userRole,
  onCloseClick,
  onGetManagerClick,
}: DailyClosingBannerProps) {
  // Tick once a minute so the countdown text stays fresh without
  // hammering React Query for unrelated reasons.
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(t)
  }, [])

  if (!currentDay) return null
  if (currentDay.status === 'closed') return null

  const minutesToCloseout = computeMinutesToCloseout(now, closeoutHour)
  if (minutesToCloseout > 4 * 60) return null

  const isManager = userRole === 'admin' || userRole === 'manager'
  const tone: 'warning' | 'critical' =
    minutesToCloseout > 30 ? 'warning' : 'critical'

  const wrapperCls = cn(
    'flex flex-wrap items-center justify-between gap-3 border-b px-6 py-3 text-sm font-medium',
    tone === 'warning' &&
      'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/60 dark:text-amber-100',
    tone === 'critical' &&
      'border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-900 dark:bg-rose-950/60 dark:text-rose-100',
  )

  const Icon = tone === 'critical' ? AlertTriangle : Clock
  const iconCls = cn(
    'h-4 w-4 shrink-0',
    tone === 'warning' && 'text-amber-600',
    tone === 'critical' && 'text-rose-600',
  )

  const remaining = formatRemaining(minutesToCloseout)
  const headline =
    tone === 'critical'
      ? `Daily Closing auto-locks in ${remaining}!`
      : `Daily Closing not done — auto-locks in ${remaining}.`

  return (
    <div className={wrapperCls}>
      <div className="flex min-w-0 items-center gap-2">
        <Icon className={iconCls} aria-hidden="true" />
        <span className="truncate">{headline}</span>
      </div>
      {isManager ? (
        <Button
          size="sm"
          onClick={onCloseClick}
          className={cn(
            'h-8 px-3',
            tone === 'critical' && 'bg-rose-600 hover:bg-rose-700 text-white',
            tone === 'warning' && 'bg-amber-600 hover:bg-amber-700 text-white',
          )}
        >
          <Lock className="mr-1.5 h-3.5 w-3.5" /> Close now
        </Button>
      ) : (
        <Button
          size="sm"
          variant="outline"
          onClick={onGetManagerClick}
          className="h-8 px-3"
        >
          Get manager
        </Button>
      )}
    </div>
  )
}

/**
 * Minutes from `now` until the next occurrence of `closeoutHour` in the
 * user's local clock. Uses device time — matches how the rest of the
 * frontend handles "now" (no business-tz helper on the frontend; backend
 * is the source of truth for `business_date` boundaries).
 */
function computeMinutesToCloseout(now: Date, closeoutHour: number): number {
  const target = new Date(now)
  target.setHours(closeoutHour, 0, 0, 0)
  if (target.getTime() <= now.getTime()) {
    target.setDate(target.getDate() + 1)
  }
  return Math.max(0, Math.round((target.getTime() - now.getTime()) / 60_000))
}

function formatRemaining(minutes: number): string {
  if (minutes < 60) return `${minutes}m`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}
